import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowUpRight } from "lucide-react";
import { api, imgUrl, formatApiError } from "@/api";
import { FadeUp } from "@/components/Reveal";

const SESSIONS = [
  { id: "automotive", label: "Automotive Session" },
  { id: "personal", label: "Personal / Portrait Session" },
  { id: "hybrid", label: "Hybrid Editorial — car + you" },
];
const PACKAGES = [
  { id: "essential", label: "Essential — 1 hr, 15 edited frames" },
  { id: "full-editorial", label: "Full Editorial — half day, 40 frames" },
  { id: "collector", label: "Private Collector Shoot — full day" },
];

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-3 text-sm text-bone placeholder:text-faint transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1";

export default function Book() {
  const [params] = useSearchParams();
  const photoId = params.get("photo");
  const sess = params.get("session");
  const [photo, setPhoto] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    session_type: ["automotive", "personal", "hybrid"].includes(sess) ? sess : "automotive",
    package: "essential",
    message: "",
  });

  useEffect(() => {
    if (photoId) api.get(`/photos/${photoId}`).then((r) => setPhoto(r.data)).catch(() => {});
  }, [photoId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...form, photo_id: photoId || null });
      setDone(data.booking);
      toast.success("Request sent — a confirmation is on its way to your inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="book-page">
      <section className="px-4 sm:px-8 lg:px-16 py-14 lg:py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Book a session</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-bone leading-tight">
              Let&apos;s schedule <em className="text-flame">your frame</em>
            </h1>
            <p className="mt-5 max-w-md text-sm text-ash leading-relaxed">
              Tell me what we&apos;re shooting and when. You&apos;ll get an email confirmation right away, and I&apos;ll
              personally follow up to lock in the details.
            </p>
            {photo && (
              <div className="mt-10 border border-white/10 bg-coal" data-testid="booking-photo-ref">
                <div className="overflow-hidden aspect-[4/3]">
                  <img src={imgUrl(photo.thumb)} alt={photo.title} className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">Inspired by</p>
                  <p className="font-serif text-xl text-bone mt-1">{photo.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint mt-1">{photo.subcategory}</p>
                </div>
              </div>
            )}
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <FadeUp>
              <div className="border border-flame/40 bg-coal p-8 sm:p-12" data-testid="booking-success">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-flame text-flame mb-6">
                  <Check size={20} />
                </span>
                <h2 className="font-serif text-3xl text-bone">Request received</h2>
                <p className="mt-4 text-sm text-ash leading-relaxed max-w-md">
                  Reference <span className="font-mono text-flame">{done.id.slice(0, 8).toUpperCase()}</span> — a
                  confirmation email is on its way to <span className="text-bone">{done.email}</span>. Maddie will reach
                  out shortly to confirm your {SESSIONS.find((s) => s.id === done.session_type)?.label.toLowerCase()} on{" "}
                  {done.date}.
                </p>
                <Link
                  to="/gallery"
                  data-testid="booking-success-gallery-link"
                  className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-flame hover:text-flamebright transition-colors"
                >
                  Back to the work <ArrowUpRight size={14} />
                </Link>
              </div>
            </FadeUp>
          ) : (
            <FadeUp delay={0.1}>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="booking-form">
                <div>
                  <label className={labelCls} htmlFor="bk-name">Full name</label>
                  <input id="bk-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-email">Email</label>
                  <input id="bk-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-phone">Phone</label>
                  <input id="bk-phone" data-testid="booking-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-date">Preferred date</label>
                  <input id="bk-date" data-testid="booking-date-input" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-session">Session type</label>
                  <select id="bk-session" data-testid="booking-session-select" value={form.session_type} onChange={set("session_type")} className={`${inputCls} bg-ink`}>
                    {SESSIONS.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-package">Package</label>
                  <select id="bk-package" data-testid="booking-package-select" value={form.package} onChange={set("package")} className={`${inputCls} bg-ink`}>
                    {PACKAGES.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-message">Anything else?</label>
                  <textarea id="bk-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Car model, location ideas, the vibe you're after…" className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="booking-submit-button"
                    className="bg-flame text-ink px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors duration-300 disabled:opacity-50"
                  >
                    {busy ? "Sending…" : "Send booking request"}
                  </button>
                </div>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </main>
  );
}
